import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Package, AlertTriangle, ScanLine, RefreshCw, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import FieldTaskList from "../components/field/FieldTaskList";

/**
 * Tableau de bord simplifié pour le profil "Tournée distributeurs".
 *
 * Affiche uniquement ce qui est utile à un opérateur terrain :
 * - Liste des distributeurs automatiques avec leur état (lots, capacité, alertes DLC)
 * - Accès rapide au scan + restock
 *
 * Pas de KPIs financiers, pas de production, pas de stocks centraux.
 */
export default function FieldOperatorDashboard() {
  const [distributors, setDistributors] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([api.get("/distributors"), api.get("/lots?active_only=true&slim=true")]);
      setDistributors(d.data || []);
      setLots(l.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const today = new Date();
  const dlcSoonThreshold = new Date(today);
  dlcSoonThreshold.setDate(today.getDate() + 2);

  // Pour chaque distributeur, calcule stats
  const distStats = distributors.map((d) => {
    const distLots = lots.filter((lot) => lot.distributor_id === d.id && (lot.quantity || 0) > 0);
    const totalQty = distLots.reduce((acc, l) => acc + (l.quantity || 0), 0);
    const dlcDepasseeLots = distLots.filter((l) => {
      if (!l.dlc) return false;
      return new Date(l.dlc) < today;
    });
    const dlcSoonLots = distLots.filter((l) => {
      if (!l.dlc) return false;
      const dlcDate = new Date(l.dlc);
      return dlcDate >= today && dlcDate <= dlcSoonThreshold;
    });
    return { ...d, totalQty, dlcDepasseeLots, dlcSoonLots, lotsCount: distLots.length };
  });

  // Alerte globale : nombre de distributeurs nécessitant une intervention
  const distNeedingAction = distStats.filter((d) => d.dlcDepasseeLots.length > 0 || d.dlcSoonLots.length > 0);

  return (
    <div className="space-y-6">
      {/* Header tournée du jour */}
      <div className="bg-gradient-to-br from-[#1F4E3D] to-[#173B2E] text-white rounded-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#A8C7B8]">Tournée du jour</div>
            <div className="text-2xl font-heading mt-1">{today.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}</div>
          </div>
          <div className="flex gap-2">
            <Link to="/scan">
              <button className="bg-white text-[#1F4E3D] hover:bg-[#F4F4F0] px-4 py-2 rounded text-sm font-medium flex items-center gap-2" data-testid="quick-scan">
                <ScanLine className="w-4 h-4" /> Scanner
              </button>
            </Link>
            <button
              onClick={load}
              disabled={loading}
              className="border border-[#A8C7B8] text-[#A8C7B8] hover:bg-[#173B2E] px-4 py-2 rounded text-sm font-medium flex items-center gap-2"
              data-testid="quick-refresh"
            >
              <RefreshCw className="w-4 h-4" /> Rafraîchir
            </button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-[#173B2E]/60 rounded p-3">
            <div className="text-xs text-[#A8C7B8] uppercase tracking-wide">Distributeurs</div>
            <div className="text-2xl font-mono">{distributors.length}</div>
          </div>
          <div className="bg-[#173B2E]/60 rounded p-3">
            <div className="text-xs text-[#A8C7B8] uppercase tracking-wide">Barquettes total</div>
            <div className="text-2xl font-mono">{distStats.reduce((a, d) => a + d.totalQty, 0)}</div>
          </div>
          <div className={`rounded p-3 ${distNeedingAction.length > 0 ? "bg-[#C05C47]/30 border border-[#C05C47]" : "bg-[#173B2E]/60"}`}>
            <div className="text-xs uppercase tracking-wide text-[#FCEEEA]">À surveiller</div>
            <div className="text-2xl font-mono">{distNeedingAction.length}</div>
          </div>
          <div className="bg-[#173B2E]/60 rounded p-3">
            <div className="text-xs text-[#A8C7B8] uppercase tracking-wide">DLC dépassées</div>
            <div className="text-2xl font-mono">{distStats.reduce((a, d) => a + d.dlcDepasseeLots.length, 0)}</div>
          </div>
        </div>
      </div>

      {/* Task list */}
      <FieldTaskList limit={8} />

      {/* Liste des distributeurs */}
      <div>
        <h2 className="text-lg font-heading text-[#1A1C1A] mb-3 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#1F4E3D]" />
          Distributeurs à visiter
        </h2>
        {loading ? (
          <div className="text-center py-8 text-[#A3A6A3]">Chargement…</div>
        ) : distStats.length === 0 ? (
          <div className="text-center py-8 text-[#A3A6A3] border border-dashed border-[#E5E5E0] rounded">
            Aucun distributeur automatique configuré.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="field-dist-list">
            {distStats.map((d) => (
              <Link
                key={d.id}
                to={`/distributeurs/${d.id}`}
                className="border border-[#E5E5E0] rounded p-4 hover:border-[#1F4E3D] hover:shadow-sm transition-all bg-white"
                data-testid={`field-dist-${d.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-medium text-[#1A1C1A]">{d.name}</div>
                    {d.city && <div className="text-xs text-[#575A57]">{d.city}</div>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#A3A6A3] flex-shrink-0" />
                </div>

                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div className="border border-[#E5E5E0] rounded p-2 bg-[#FAFAF7]">
                    <div className="text-[9px] uppercase text-[#A3A6A3]">Barquettes</div>
                    <div className="font-mono text-lg text-[#1F4E3D]">{d.totalQty}</div>
                  </div>
                  <div className={`rounded p-2 ${d.dlcSoonLots.length > 0 ? "bg-[#FBF4E9] border border-[#E8D3A8]" : "bg-[#FAFAF7] border border-[#E5E5E0]"}`}>
                    <div className="text-[9px] uppercase text-[#8C6B2F]">DLC ≤2 j</div>
                    <div className={`font-mono text-lg ${d.dlcSoonLots.length > 0 ? "text-[#8C6B2F]" : "text-[#A3A6A3]"}`}>{d.dlcSoonLots.length}</div>
                  </div>
                  <div className={`rounded p-2 ${d.dlcDepasseeLots.length > 0 ? "bg-[#FCEEEA] border border-[#E8C1B4]" : "bg-[#FAFAF7] border border-[#E5E5E0]"}`}>
                    <div className="text-[9px] uppercase text-[#C05C47]">Périmés</div>
                    <div className={`font-mono text-lg ${d.dlcDepasseeLots.length > 0 ? "text-[#C05C47]" : "text-[#A3A6A3]"}`}>{d.dlcDepasseeLots.length}</div>
                  </div>
                </div>

                {(d.dlcDepasseeLots.length > 0 || d.dlcSoonLots.length > 0) && (
                  <div className="mt-3 pt-2 border-t border-[#E5E5E0] text-xs text-[#8C6B2F] flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Intervention recommandée
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
